import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { PublicNavbar } from '../components/PublicNavbar'
import { PublicFooter } from '../components/PublicFooter'
import { PasswordRequirements } from '../components/PasswordRequirements'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { isPasswordValid } from '../lib/password'

/**
 * Landing page for the password-reset email link. Supabase establishes a temporary
 * recovery session from the URL on load (detectSessionInUrl), so an authenticated
 * session here means the link is valid and we can call updateUser to set a new password.
 */
export const ResetPasswordPage = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const passwordsMismatch = confirmPassword.length > 0 && confirmPassword !== password
  const canSubmit = isPasswordValid(password) && confirmPassword === password

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    if (!isPasswordValid(password)) {
      setError('Password does not meet all the requirements.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setIsSubmitting(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) throw updateError
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      setError(err?.message ?? 'Failed to reset password. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PublicNavbar />
      <Container
        maxWidth="sm"
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 520 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {isLoading ? (
              <Typography color="text.secondary">Loading…</Typography>
            ) : !isAuthenticated ? (
              <Stack spacing={2}>
                <Typography variant="h4">Reset link invalid</Typography>
                <Typography color="text.secondary">
                  This password reset link is invalid or has expired. Request a new one from the
                  login page.
                </Typography>
                <Button variant="contained" color="warning" onClick={() => navigate('/login')}>
                  Back to login
                </Button>
              </Stack>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <Typography variant="h4">Choose a new password</Typography>
                  {error && <Alert severity="error">{error}</Alert>}
                  <TextField
                    label="New password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <PasswordRequirements password={password} />
                  <TextField
                    label="Confirm new password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    error={passwordsMismatch}
                    helperText={passwordsMismatch ? 'Passwords do not match' : ' '}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    type="submit"
                    disabled={isSubmitting || !canSubmit}
                  >
                    {isSubmitting ? 'Updating…' : 'Update password'}
                  </Button>
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
      <PublicFooter />
    </Box>
  )
}
